import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Fingerprint, Trash2, X } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import {
  AnalysisFilter,
  AnalysisRow,
  DataRoom,
  DocumentRole,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import AnalysisDetail from "./AnalysisDetail";
import { StatusBadge } from "./analysisView";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Every run that has been made, searchable.
 *
 * The table exists for one question the processing test cannot answer: is this run better than the
 * last one? That means comparing — the same document imported twice, a document re-run after its
 * cues were sharpened, two versions of a lease that ought to resolve to the same objects. So the
 * columns are chosen for comparison rather than for completeness: which generation of the catalogue
 * decided it, how sure it was, how much it found.
 *
 * The table itself writes nothing: a run is a log of something that happened. Removing one is the
 * single exception and is asked for before it is done — what deletion is to *mean* is still open,
 * and what it does today is narrow: the run and its staged objects go, the document stays.
 */

const STATUSES = ["completed", "undecided", "failed", "aborted", "running"];
const PAGE_SIZE = 50;

const ALL = "__all__";

interface Properties {
  /** Set when the processing test linked here, so the table opens on exactly that run. */
  initialAnalysisId?: string | null;
}

export default function AnalysesTab({ initialAnalysisId }: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();

  const [rows, setRows] = useState<AnalysisRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [roles, setRoles] = useState<DocumentRole[]>([]);
  const [dataRooms, setDataRooms] = useState<DataRoom[]>([]);
  const [openAnalysis, setOpenAnalysis] = useState<string | null>(initialAnalysisId ?? null);
  const [removing, setRemoving] = useState<AnalysisRow | null>(null);

  const [filter, setFilter] = useState<AnalysisFilter>({
    page: 1,
    pageSize: PAGE_SIZE,
    sort: "startedAt",
    direction: "desc",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAnalyses(filter);
      setRows(result.analyses ?? []);
      setTotal(result.total ?? 0);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [roleResult, roomResult] = await Promise.all([
          api.getDocumentRoles(),
          api.getDataRooms(),
        ]);
        setRoles(roleResult.roles ?? []);
        setDataRooms(roomResult.dataRooms ?? []);
      } catch (error) {
        // The filters are a convenience; a table that loads without them beats no table.
        pushError(error);
      }
    };

    void loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Any change to a filter starts again at page one — page 7 of a different set means nothing. */
  const narrow = (changes: Partial<AnalysisFilter>) =>
    setFilter((current) => ({ ...current, ...changes, page: 1 }));

  const sortBy = (column: string) =>
    setFilter((current) => ({
      ...current,
      sort: column,
      direction: current.sort === column && current.direction === "desc" ? "asc" : "desc",
      page: 1,
    }));

  const subtypes = useMemo(() => {
    const role = roles.find((entry) => entry.name === filter.roleName);
    return (role?.subtypes ?? []).map((subtype) => subtype.name).filter(Boolean);
  }, [roles, filter.roleName]);

  const roomName = (id: string | null) =>
    dataRooms.find((room) => room._id === id)?.name ?? (id ? "—" : "");

  const pages = Math.max(Math.ceil(total / (filter.pageSize ?? PAGE_SIZE)), 1);

  /** The filters set by clicking a cell rather than by choosing from a list. */
  const chips: Array<{ key: keyof AnalysisFilter; label: string }> = [];
  if (filter.contentHash) {
    chips.push({ key: "contentHash", label: t("documentAnalysisPanel.analyses.sameDocument") });
  }
  if (filter.dataRoomId) {
    chips.push({
      key: "dataRoomId",
      label: t("documentAnalysisPanel.analyses.sameDataRoom", { name: roomName(filter.dataRoomId) }),
    });
  }
  if (filter.boundObjectId) {
    chips.push({ key: "boundObjectId", label: t("documentAnalysisPanel.analyses.boundToObject") });
  }

  const remove = async () => {
    if (!removing) return;

    try {
      await api.deleteAnalysis(removing._id);
      setRemoving(null);
      await load();
    } catch (error) {
      pushError(error);
    }
  };

  const header = (column: string, label: string) => (
    <TableHead>
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium hover:underline"
        onClick={() => sortBy(column)}
      >
        {label}
        {filter.sort === column ? (
          filter.direction === "asc" ? (
            <ArrowUp className="h-3 w-3" aria-hidden />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden />
          )
        ) : null}
      </button>
    </TableHead>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1">
          <Label htmlFor="analysisSearch">{t("documentAnalysisPanel.analyses.search")}</Label>
          <Input
            id="analysisSearch"
            value={filter.q ?? ""}
            placeholder={t("documentAnalysisPanel.analyses.searchPlaceholder")}
            onChange={(event) => narrow({ q: event.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="analysisRole">{t("documentAnalysisPanel.columns.role")}</Label>
          <Select
            value={filter.roleName ?? ALL}
            // Changing the role invalidates the subtype: a subtype belongs to exactly one role.
            onValueChange={(value) =>
              narrow({ roleName: value === ALL ? undefined : value, subtypeName: undefined })
            }
          >
            <SelectTrigger id="analysisRole" className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("documentAnalysisPanel.analyses.allRoles")}</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role.name} value={role.name}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {subtypes.length > 0 ? (
          <div>
            <Label htmlFor="analysisSubtype">{t("documentAnalysisPanel.analyses.subtype")}</Label>
            <Select
              value={filter.subtypeName ?? ALL}
              onValueChange={(value) => narrow({ subtypeName: value === ALL ? undefined : value })}
            >
              <SelectTrigger id="analysisSubtype" className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("documentAnalysisPanel.analyses.allSubtypes")}</SelectItem>
                {subtypes.map((subtype) => (
                  <SelectItem key={subtype} value={subtype}>
                    {subtype}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {dataRooms.length > 0 ? (
          <div>
            <Label htmlFor="analysisDataRoom">{t("documentAnalysisPanel.analyses.dataRoom")}</Label>
            <Select
              value={filter.dataRoomId ?? ALL}
              onValueChange={(value) => narrow({ dataRoomId: value === ALL ? undefined : value })}
            >
              <SelectTrigger id="analysisDataRoom" className="w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("documentAnalysisPanel.analyses.allDataRooms")}</SelectItem>
                {dataRooms.map((room) => (
                  <SelectItem key={room._id} value={room._id}>
                    {room.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div>
          <Label htmlFor="analysisFrom">{t("documentAnalysisPanel.analyses.from")}</Label>
          <Input
            id="analysisFrom"
            type="date"
            className="w-[160px]"
            value={filter.from ?? ""}
            onChange={(event) => narrow({ from: event.target.value || undefined })}
          />
        </div>

        <div>
          <Label htmlFor="analysisTo">{t("documentAnalysisPanel.analyses.to")}</Label>
          <Input
            id="analysisTo"
            type="date"
            className="w-[160px]"
            value={filter.to ?? ""}
            onChange={(event) => narrow({ to: event.target.value || undefined })}
          />
        </div>
      </div>

      {/* Statuses as toggles rather than a multi-select: there are five of them, they are the most
          used filter, and a click beats opening a list to tick a box. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("documentAnalysisPanel.analyses.status")}</span>
        {STATUSES.map((status) => {
          const active = (filter.status ?? []).indexOf(status) >= 0;

          return (
            <Button
              key={status}
              size="sm"
              variant={active ? "default" : "outline"}
              aria-pressed={active}
              onClick={() =>
                narrow({
                  status: active
                    ? (filter.status ?? []).filter((entry) => entry !== status)
                    : [...(filter.status ?? []), status],
                })
              }
            >
              {t(`documentAnalysisPanel.analyses.statusName.${status}`)}
            </Button>
          );
        })}

        {chips.map((chip) => (
          <span
            key={chip.key as string}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs"
          >
            {chip.label}
            <button
              type="button"
              aria-label={t("documentAnalysisPanel.analyses.removeFilter", { filter: chip.label })}
              onClick={() => narrow({ [chip.key]: undefined } as Partial<AnalysisFilter>)}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
      </div>

      <p className="text-sm text-muted-foreground">
        {t("documentAnalysisPanel.analyses.summary", { total })}
      </p>

      {loading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {header("startedAt", t("documentAnalysisPanel.analyses.startedAt"))}
                {header("document.fileName", t("documentAnalysisPanel.analyses.document"))}
                {header("roleName", t("documentAnalysisPanel.analyses.roleAndSubtype"))}
                <TableHead>{t("documentAnalysisPanel.analyses.confidences")}</TableHead>
                {header("status", t("documentAnalysisPanel.analyses.status"))}
                <TableHead>{t("documentAnalysisPanel.analyses.counts")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.analyses.dataRoom")}</TableHead>
                <TableHead>{t("documentAnalysisPanel.analyses.generation")}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                    {t("documentAnalysisPanel.analyses.none")}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => <AnalysisTableRow
                  key={row._id}
                  row={row}
                  roomName={roomName}
                  onOpen={() => setOpenAnalysis(row._id)}
                  onSameDocument={() => narrow({ contentHash: row.document?.contentHash })}
                  onSameDataRoom={() => narrow({ dataRoomId: row.dataRoomId ?? undefined })}
                  onRemove={() => setRemoving(row)}
                />)
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {pages > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={(filter.page ?? 1) <= 1}
            onClick={() => setFilter((current) => ({ ...current, page: (current.page ?? 1) - 1 }))}
          >
            {t("documentAnalysisPanel.analyses.previous")}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t("documentAnalysisPanel.analyses.page", { page: filter.page ?? 1, pages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={(filter.page ?? 1) >= pages}
            onClick={() => setFilter((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}
          >
            {t("documentAnalysisPanel.analyses.next")}
          </Button>
        </div>
      ) : null}

      <AnalysisDetail analysisId={openAnalysis} onClose={() => setOpenAnalysis(null)} />

      <AlertDialog open={!!removing} onOpenChange={(open) => (open ? null : setRemoving(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("documentAnalysisPanel.analyses.removeTitle", { name: removing?.document?.fileName })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("documentAnalysisPanel.analyses.removeExplanation")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>{t("global.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const formatMoment = (value: string | null) =>
  value ? new Date(value).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—";

const formatConfidence = (value: number | null) =>
  typeof value === "number" ? value.toFixed(2) : "—";

function AnalysisTableRow({
  row,
  roomName,
  onOpen,
  onSameDocument,
  onSameDataRoom,
  onRemove,
}: {
  row: AnalysisRow;
  roomName: (id: string | null) => string;
  onOpen: () => void;
  onSameDocument: () => void;
  onSameDataRoom: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const duration =
    row.finishedAt && row.startedAt
      ? `${((new Date(row.finishedAt).getTime() - new Date(row.startedAt).getTime()) / 1000).toFixed(1)} s`
      : "";

  const subtype = [row.subtypeName, row.parameterValue].filter(Boolean).join(": ");

  return (
    <TableRow className="cursor-pointer align-top" onClick={onOpen}>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="whitespace-nowrap text-sm">{formatMoment(row.startedAt)}</span>
            </TooltipTrigger>
            <TooltipContent>
              {duration || t("documentAnalysisPanel.analyses.unfinished")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-1">
          <span className="max-w-[260px] truncate text-sm" title={row.document?.fileName}>
            {row.document?.fileName}
          </span>

          {/* The hash is identity: clicking it asks for every run over the same bytes, which is
              the comparison this table exists for. */}
          {row.document?.contentHash ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={t("documentAnalysisPanel.analyses.sameDocument")}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSameDocument();
                    }}
                  >
                    <Fingerprint className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="font-mono text-xs">
                  {row.document.contentHash.slice(0, 23)}…
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      </TableCell>

      <TableCell>
        {row.roleName ? (
          <span className="text-sm">
            {row.roleName}
            {subtype ? <span className="text-muted-foreground"> → {subtype}</span> : null}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>

      <TableCell className="whitespace-nowrap font-mono text-xs">
        {formatConfidence(row.roleConfidence)} / {formatConfidence(row.subtypeConfidence)}
      </TableCell>

      <TableCell>
        <StatusBadge status={row.status} failedStep={row.failedStep} />
      </TableCell>

      <TableCell className="whitespace-nowrap font-mono text-xs">
        {row.extractedCount ?? 0} / {row.boundCount ?? 0}
      </TableCell>

      <TableCell>
        {row.dataRoomId ? (
          <button
            type="button"
            className="text-sm hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onSameDataRoom();
            }}
          >
            {roomName(row.dataRoomId)}
          </button>
        ) : null}
      </TableCell>

      <TableCell className="text-xs text-muted-foreground">
        {row.provenance?.roleGeneration ?? ""}
      </TableCell>

      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive"
          aria-label={t("documentAnalysisPanel.analyses.removeTitle", {
            name: row.document?.fileName,
          })}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </TableCell>
    </TableRow>
  );
}
